/**
 * §7.7 Variant Tracker. Pulls a Tier 2 bundled reference genome,
 * aligns a candidate sequence (user-supplied or pre-baked mutated
 * variant) with Needleman–Wunsch, calls SNVs / indels, and checks
 * CDC primer integrity. Exercises @biomax/plugin-pathogen-genomics
 * + @biomax/plugin-bioseq end-to-end in the browser.
 */

import { useMemo, useState } from 'react';
import { PinToBriefButton } from '../pack/PinToBriefButton';
import {
  loadBundledGenome, hasBundledGenome, GENOME_REGISTRY,
} from '@biomax/plugin-pathogen-genomics';
import {
  needlemanWunsch, callVariants, findPrimerMatches,
} from '@biomax/plugin-bioseq';
import { findPathogen } from '@biomax/plugin-pathogen-database';

const PRIMER_SETS: Record<string, { label: string; sequence: string; source: string }[]> = {
  'sars-cov-2-wildtype': [
    { label: 'CDC N1-F', sequence: 'GACCCCAAAATCAGCGAAAT', source: 'CDC 2019-nCoV N1 forward' },
    { label: 'CDC N2-F', sequence: 'TTACAAACATTGGCCGCAAA', source: 'CDC 2019-nCoV N2 forward' },
  ],
  'measles': [
    { label: 'WHO N450-F', sequence: 'AGGACCAGGAAACAGGGTTGTA', source: 'WHO measles N450 surveillance primer (illustrative)' },
  ],
  'ebola-zaire': [
    { label: 'CDC NP-F', sequence: 'TCTGACATGGATTACCACAAGAT', source: 'CDC Ebola NP forward (illustrative)' },
  ],
};

// Region of reference to scope alignment (full genome NW would be expensive).
const ANALYSIS_REGION_BP = 500;
const ANALYSIS_OFFSET_BP = 1000;

type MutationMode = 'none' | 'snv-5' | 'snv-25' | 'deletion-30bp';

export function VariantTracker() {
  const bundledIds = Object.keys(GENOME_REGISTRY).filter(hasBundledGenome);
  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [mutationMode, setMutationMode] = useState<MutationMode>('snv-5');

  const view = useMemo(() => {
    if (!hasBundledGenome(pathogenId)) return null;
    const genome = loadBundledGenome(pathogenId);
    const pathogen = findPathogen(pathogenId);

    const ref = genome.sequence.slice(ANALYSIS_OFFSET_BP, ANALYSIS_OFFSET_BP + ANALYSIS_REGION_BP);

    // Apply chosen mutation mode to a copy.
    let variant = ref;
    if (mutationMode === 'snv-5') {
      variant = introduceSnvs(ref, 5);
    } else if (mutationMode === 'snv-25') {
      variant = introduceSnvs(ref, 25);
    } else if (mutationMode === 'deletion-30bp') {
      variant = ref.slice(0, 200) + ref.slice(230);
    }

    const t0 = performance.now();
    const alignment = needlemanWunsch(ref, variant);
    const variants = callVariants(alignment);
    const alignMs = performance.now() - t0;

    // Primer integrity check: do reference primers still find a match on the variant?
    const primers = PRIMER_SETS[pathogenId] ?? [];
    const primerStatus = primers.map(p => {
      const refMatches = findPrimerMatches(p.sequence, genome.sequence, 0).length;
      const variantMatches = findPrimerMatches(p.sequence, genome.sequence.slice(0, ANALYSIS_OFFSET_BP) + variant + genome.sequence.slice(ANALYSIS_OFFSET_BP + ANALYSIS_REGION_BP), 0).length;
      return { ...p, refMatches, variantMatches, intact: variantMatches === refMatches && refMatches > 0 };
    });

    return {
      genome, pathogen, alignment, variants, alignMs, primerStatus,
    };
  }, [pathogenId, mutationMode]);

  if (!view) return <div>No Tier 2 genome bundled for this pathogen.</div>;

  const snvCount = view.variants.filter(v => v.type === 'SNV').length;
  const insCount = view.variants.filter(v => v.type === 'insertion').length;
  const delCount = view.variants.filter(v => v.type === 'deletion').length;

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.7</span>
        <h1 className="surface__title">Variant Tracker</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Live Needleman–Wunsch + variant caller against {view.genome.accession} ·{' '}
          {view.genome.sequenceLengthBp.toLocaleString()} bp reference ·
          analysing {ANALYSIS_REGION_BP} bp window from offset {ANALYSIS_OFFSET_BP}
        </span>
        <span style={{ flex: 1 }} />
        <PinToBriefButton item={{
          id: `variant-${view.genome.accession}-snv${snvCount}-ins${insCount}-del${delCount}`,
          surface: '§7.7',
          label: `Variants vs ${view.genome.accession}: ${snvCount} SNV, ${insCount} ins, ${delCount} del`,
          value: snvCount + insCount + delCount,
          context: { accession: view.genome.accession, snv: snvCount, ins: insCount, del: delCount },
        }} />
      </div>

      <div className="panel panel--accent" style={{ marginBottom: 'var(--bm-space-4)' }}>
        <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--bm-signal)' }}>SYNTHETIC SCENARIO — not surveillance data.</strong> The candidate
          sequence is the real bundled reference with user-chosen mutations introduced, to demonstrate the alignment +
          variant-calling + primer-integrity engine. The reference genome is real (NCBI-verified); the "variant" is not observed.
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Configuration</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen (Tier 2 bundled)</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {bundledIds.map(id => {
                const entry = GENOME_REGISTRY[id]!;
                return <option key={id} value={id}>{id} · {entry.refseq ?? entry.genbank ?? '—'}</option>;
              })}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Synthetic mutation</span>
            <select value={mutationMode} onChange={e => setMutationMode(e.target.value as MutationMode)}>
              <option value="none">No mutations (sanity check)</option>
              <option value="snv-5">5 random SNVs</option>
              <option value="snv-25">25 random SNVs</option>
              <option value="deletion-30bp">30 bp deletion</option>
            </select>
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Alignment identity</div>
          <div className="kpi__value kpi__value--accent">{(view.alignment.identity * 100).toFixed(2)}%</div>
          <div className="kpi__sub">global · NW score {view.alignment.score}</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">SNVs called</div>
          <div className="kpi__value kpi__value--signal">{snvCount}</div>
          <div className="kpi__sub">single-nucleotide variants</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Indels</div>
          <div className="kpi__value">{insCount + delCount}</div>
          <div className="kpi__sub">{insCount} ins · {delCount} del</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Alignment time</div>
          <div className="kpi__value">{view.alignMs.toFixed(0)} ms</div>
          <div className="kpi__sub">{ANALYSIS_REGION_BP}² cell DP matrix</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Primer integrity</div>
          <div className={`kpi__value ${view.primerStatus.every(p => p.intact) ? 'kpi__value--accent' : 'kpi__value--danger'}`}>
            {view.primerStatus.filter(p => p.intact).length} / {view.primerStatus.length}
          </div>
          <div className="kpi__sub">surveillance primers still match</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Called variants</span>
          <span className="panel__meta">first 30 shown</span>
        </div>
        {view.variants.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--bm-text-muted)', padding: 'var(--bm-space-3)' }}>
            No variants — reference identical to candidate sequence.
          </div>
        ) : (
          <table className="dtable">
            <thead>
              <tr>
                <th className="num">Pos</th>
                <th>Type</th>
                <th>Ref</th>
                <th>Alt</th>
              </tr>
            </thead>
            <tbody>
              {view.variants.slice(0, 30).map((v, i) => (
                <tr key={i}>
                  <td className="num">{v.position}</td>
                  <td>{v.type}</td>
                  <td style={{ fontFamily: 'var(--bm-font-mono)' }}>{v.ref}</td>
                  <td style={{ fontFamily: 'var(--bm-font-mono)' }}>{v.alt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Genome verification</span>
          <span className="panel__meta">scripts/verify-genomes.ts · local + NCBI round-trip</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--bm-text)', padding: '8px 12px', lineHeight: 1.6 }}>
          Active reference: <strong>{view.genome.accession}</strong> · {view.genome.sequenceLengthBp.toLocaleString()} bp ·
          fetched {view.genome.fetchedAt}.
          {' '}All 8 bundled Tier 2 genomes pass the verifier-first invariant
          (FASTA length on disk == metadata sequenceLengthBp == NCBI E-utils
          efetch length). Run <code>npm run verify:genomes</code> in
          packages/plugin-pathogen-genomics to revalidate against live NCBI.
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Surveillance primer integrity</span>
          <span className="panel__meta">CDC + WHO published primer sets (exact-match v0.1; mismatch tolerance v0.4)</span>
        </div>
        {view.primerStatus.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--bm-text-muted)', padding: 'var(--bm-space-3)' }}>
            No primer set bundled for this pathogen yet.
          </div>
        ) : (
          <table className="dtable">
            <thead>
              <tr>
                <th>Primer</th>
                <th>Sequence</th>
                <th>Source</th>
                <th className="num">Ref matches</th>
                <th className="num">Variant matches</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {view.primerStatus.map(p => (
                <tr key={p.label}>
                  <td>{p.label}</td>
                  <td style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>{p.sequence}</td>
                  <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{p.source}</td>
                  <td className="num">{p.refMatches}</td>
                  <td className="num">{p.variantMatches}</td>
                  <td style={{ color: p.intact ? 'var(--bm-accent)' : 'var(--bm-danger)' }}>
                    {p.intact ? 'INTACT' : 'IMPACTED'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Deterministic SNV introducer — seed is fixed so the same mutation count
// always produces the same mutated sequence (reproducibility invariant).
function introduceSnvs(ref: string, count: number): string {
  let s = ref;
  let seed = 12345;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  for (let i = 0; i < count; i++) {
    const pos = Math.floor(rand() * s.length);
    const cur = s[pos]!;
    const alts = ['A', 'C', 'G', 'T'].filter(b => b !== cur);
    const alt = alts[Math.floor(rand() * alts.length)]!;
    s = s.slice(0, pos) + alt + s.slice(pos + 1);
  }
  return s;
}
